/**
 * Plain-language presentation rails — shared across every operator surface.
 *
 * This is the FOUNDATION for removing analyst / statistics / engineering jargon from
 * the operator UI. Every screen consumes these helpers so five screens can't invent five
 * different wordings (`pp` vs `ppt` vs `ppts`, `x` vs `×`). The rules locked here are
 * documented in `docs/revenue-vocabulary.md` § "Plain-language presentation".
 *
 * Pure display helpers — NO arithmetic on money or rate data (CLAUDE.md Data Integrity).
 * Percentages that arrive already-computed from the backend are rounded for a readable
 * label only (the same thing the calendar has always done via `.toFixed(0)`).
 */

// ─── Pace: one worded five-band scale, never a bare multiplier ────────────────
//
// The bare ratio (`0.53x`) NEVER appears on an operator surface — it lives only inside
// `EngineDetails`. Everywhere a pace/ratio is shown, it is one of these five words paired
// with a plain comparison (see `paceComparison`). Thresholds are the ones already used by
// the calendar reasoning (well ahead ≥1.2, ahead ≥1.05, on track ≥0.95, behind ≥0.8).

export type PaceKey = 'well_ahead' | 'ahead' | 'on_track' | 'behind' | 'well_behind' | 'unknown'
export type Tone = 'positive' | 'neutral' | 'negative'

export interface PaceBand {
  key: PaceKey
  /** Operator-facing words — the ONLY pace vocabulary allowed on a glance surface. */
  label: string
  tone: Tone
}

const PACE_BANDS: Record<PaceKey, PaceBand> = {
  well_ahead: { key: 'well_ahead', label: 'Well ahead', tone: 'positive' },
  ahead: { key: 'ahead', label: 'Ahead', tone: 'positive' },
  on_track: { key: 'on_track', label: 'On track', tone: 'neutral' },
  behind: { key: 'behind', label: 'Behind', tone: 'negative' },
  well_behind: { key: 'well_behind', label: 'Well behind', tone: 'negative' },
  unknown: { key: 'unknown', label: 'Not enough history', tone: 'neutral' },
}

/**
 * Classify a pace/velocity ratio into the locked five-band worded scale.
 * `null`/`undefined`/non-finite → the `unknown` band (never a fabricated "on track").
 */
export function paceBand(ratio: number | null | undefined): PaceBand {
  if (ratio == null || !Number.isFinite(ratio)) return PACE_BANDS.unknown
  if (ratio >= 1.2) return PACE_BANDS.well_ahead
  if (ratio >= 1.05) return PACE_BANDS.ahead
  if (ratio >= 0.95) return PACE_BANDS.on_track
  if (ratio >= 0.8) return PACE_BANDS.behind
  return PACE_BANDS.well_behind
}

// ─── A relative judgment on a glance surface must name its reference ──────────
//
// "On track" is a comparison, and on its own it never says with WHAT. That is the origin
// finding behind Last-Minute Clearance (docs/plans/2026-08-13-last-minute-clearance.md §7): an
// operator read a calm pace label over four unsold rooms and concluded the engine was asleep,
// because "on track" against a 75% finish and four empty rooms are the same fact and only one of
// them was on the screen.
//
// So the band gains a clause that states the reference IN ROOMS. Rooms are what an operator
// counts; a percentage of a capacity they have to hold in their head is the abstraction that hid
// the problem in the first place.

export interface PaceBandWithReference extends PaceBand {
  /** "a weekday usually has about 8–9 of 14 rooms by now" — null when it cannot be computed. */
  reference: string | null
  /** The band and its reference as one line. Falls back to the bare label. */
  sentence: string
}

export interface PaceReferenceOptions {
  /** The expected occupancy % AT THIS POINT IN THE BOOKING WINDOW — the figure the band judged. */
  baselinePct: number | null | undefined
  /** Rooms available on the night — the denominator the rooms figure is "of". */
  capacity: number | null | undefined
  /**
   * What the caller can HONESTLY call the reference — 'weekday' / 'weekend' / 'Wednesday'.
   * Omit it unless the baseline being passed really is that day type's: naming a Wednesday over
   * an all-days figure would be a claim the number does not support.
   */
  dayName?: string | null
}

/**
 * The band, plus the reference it was judged against, in absolute rooms.
 *
 * `paceBand` itself is deliberately UNCHANGED — a dozen surfaces render the bare five-word scale
 * in table cells and chart labels where a sentence does not fit, and the locked vocabulary is
 * those five words. This is additive: the clause rides along where there is room for it.
 *
 * ROOM-COUNT MATH IS ALLOWED HERE, and it is the documented exception to the no-frontend-math
 * rule (CLAUDE.md Data Integrity): that rule governs MONEY and rate data, whose canonical values
 * are computed and rounded by the pipeline. This is a count of rooms from an occupancy percentage
 * the backend already sent — no monetary value is derived, and nothing here can disagree with a
 * price.
 *
 * The range is the honest one: an occupancy percentage of a capacity rarely lands on a whole
 * room, so 75% of 14 is 10.5 rooms and reads "about 10–11 of 14 rooms". When it does land whole
 * the range collapses and it reads "about 7 of 14 rooms".
 */
export function paceBandWithReference(band: PaceBand, opts: PaceReferenceOptions): PaceBandWithReference {
  const reference = paceReference(band, opts)
  return { ...band, reference, sentence: reference ? `${band.label} — ${reference}` : band.label }
}

function paceReference(band: PaceBand, { baselinePct, capacity, dayName }: PaceReferenceOptions): string | null {
  // A band we could not classify has no reference to name — "Not enough history" plus a figure
  // would be a manufactured comparison.
  if (band.key === 'unknown') return null
  if (baselinePct == null || !Number.isFinite(baselinePct)) return null
  if (capacity == null || !Number.isFinite(capacity) || capacity <= 0) return null

  const rooms = (baselinePct / 100) * capacity
  const low = Math.floor(rooms)
  const high = Math.ceil(rooms)
  const span = low === high ? `${low}` : `${low}–${high}`
  const subject = dayName ? `a ${dayName} usually has` : 'you usually have'

  return `${subject} about ${span} of ${capacity} rooms by now`
}

/**
 * The plain comparison that always accompanies a pace band, e.g.
 * "32% full · usually ~61% by now". Degrades gracefully:
 *  - both known  → "32% full · usually ~61% by now"
 *  - only actual → "32% full"
 *  - only usual  → "usually ~61% full by now"
 *  - neither     → "" (caller shows the band label alone)
 */
export function paceComparison(occPct: number | null | undefined, baselinePct: number | null | undefined): string {
  const haveOcc = occPct != null && Number.isFinite(occPct)
  const haveBase = baselinePct != null && Number.isFinite(baselinePct)
  const occ = haveOcc ? Math.round(occPct as number) : null
  const base = haveBase ? Math.round(baselinePct as number) : null

  if (occ != null && base != null) return `${occ}% full · usually ~${base}% by now`
  if (occ != null) return `${occ}% full`
  if (base != null) return `usually ~${base}% full by now`
  return ''
}

// ─── Pickup: rooms, not percentage points ─────────────────────────────────────
//
// Operators count rooms. "+2 rooms this week", never "+3pp 7d". A percentage-points delta
// survives only inside the diagnostics disclosure.

/** "+2 rooms" / "-1 room" / "no change" / "—" with correct singular/plural. */
export function formatPickupRooms(rooms: number | null | undefined): string {
  if (rooms == null || !Number.isFinite(rooms)) return '—'
  if (rooms === 0) return 'no change'
  const n = Math.abs(rooms)
  const unit = n === 1 ? 'room' : 'rooms'
  const sign = rooms > 0 ? '+' : '-'
  return `${sign}${n} ${unit}`
}

// ─── Points: the ONE spelling for a percentage-points delta ───────────────────
//
// "pts" replaces every variant of `pp` / `ppt` / `ppts`. Used ONLY where a points-delta is
// genuinely the right unit (e.g. occupancy vs last year) — pickup is rooms, not pts.

export interface FormatPointsOptions {
  /** Show a "+" in front of positive values (default true). */
  signed?: boolean
  /** What to render for exactly zero (default "0 pts"). */
  zeroLabel?: string
}

/** "+12 pts" / "-3 pts" / "0 pts" / "—". Displays the number the backend sent, no rounding. */
export function formatPoints(pts: number | null | undefined, opts: FormatPointsOptions = {}): string {
  if (pts == null || !Number.isFinite(pts)) return '—'
  const { signed = true, zeroLabel = '0 pts' } = opts
  if (pts === 0) return zeroLabel
  const sign = pts > 0 ? (signed ? '+' : '') : '-'
  return `${sign}${Math.abs(pts)} pts`
}

// ─── Nights: spelled, never a multiplier glyph ────────────────────────────────

/** Lead time in plain words: "Past date" / "Today" / "1 night away" / "14 nights away". */
export function nightsAway(days: number): string {
  if (!Number.isFinite(days)) return '—'
  if (days < 0) return 'Past date'
  if (days === 0) return 'Today'
  return days === 1 ? '1 night away' : `${days} nights away`
}

/** "Min 2 nights" / "Min 1 night". */
export function minNights(n: number): string {
  return `Min ${n} ${n === 1 ? 'night' : 'nights'}`
}

/** "Max 3 nights" / "Max 1 night". */
export function maxNights(n: number): string {
  return `Max ${n} ${n === 1 ? 'night' : 'nights'}`
}

// ─── Kept abbreviations, each with a plain one-line definition ────────────────
//
// STLY / OTB / CTA / CTD stay on the surfaces (operators know them, or learn them once) but
// every screen that shows one attaches this definition as a tooltip or legend, so the word is
// never bare. ADR and RevPAR are universally understood and intentionally NOT listed here.

export const ABBREV_TOOLTIPS: Record<'STLY' | 'OTB' | 'CTA' | 'CTD', string> = {
  STLY: 'Same time last year — where the books stood on this date one year ago, so you compare like with like.',
  OTB: 'On the books — rooms and revenue from confirmed bookings, including nights already stayed. Not a forecast.',
  CTA: 'Closed to arrival — guests can’t check in on this date (they can still stay through it).',
  CTD: 'Closed to departure — guests can’t check out on this date (they can still stay through it).',
}
